import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CompanyTodoRow } from '../components/CompanyTodoList';
import { projectCustomerState } from '../components/ProjectCustomer';
import { FEATURE_PROJECT_WITHOUT_CUSTOMER } from '../constants/featureFlags';
import { customerApi } from '../services/api';
import type { Project } from '../types';

/**
 * "Att göra nu" for projects without a Beställare (ClickUp 869fmxjgt), shared
 * by Hem and Projektlista: add the Beställare, or remind the invited one who
 * has not verified with BankID yet. A sent reminder turns its row into a
 * confirmation that opens the project.
 */
export function useProjectCustomerTodos(projects: Project[], onChange?: () => void): CompanyTodoRow[] {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [reminded, setReminded] = useState<ReadonlySet<string>>(new Set());
  if (!FEATURE_PROJECT_WITHOUT_CUSTOMER) return [];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'sv-SE';

  return projects.flatMap((project): CompanyTodoRow[] => {
    const state = projectCustomerState(project);
    const vars = { projectNumber: project.projectNumber, project: project.description ?? '' };
    if (state === 'missing') {
      return [{
        id: 'addCustomer',
        priority: 'waiting',
        key: `add-customer-${project.id}`,
        since: project.createdAt,
        vars,
        onAction: () => navigate(`/projects/${project.id}?tab=contract&addCustomer=1`)
      }];
    }
    const invitation = project.customerInvitation;
    if (state !== 'waiting' || !invitation) return [];
    const waitingVars = {
      ...vars,
      name: invitation.name || invitation.email,
      date: new Date(invitation.sentAt).toLocaleDateString(locale, { day: 'numeric', month: 'short' })
    };
    if (reminded.has(invitation.id)) {
      return [{
        id: 'customerWaiting',
        textKey: 'customerReminded',
        priority: 'later',
        key: `customer-waiting-${project.id}`,
        since: invitation.sentAt,
        vars: waitingVars,
        onAction: () => navigate(`/projects/${project.id}?tab=contract`)
      }];
    }
    return [{
      id: 'customerWaiting',
      priority: 'later',
      key: `customer-waiting-${project.id}`,
      since: invitation.sentAt,
      vars: waitingVars,
      onAction: () => {
        void customerApi.resendInvitation(invitation.id)
          .then(() => {
            setReminded(previous => new Set(previous).add(invitation.id));
            onChange?.();
          })
          .catch(() => navigate(`/projects/${project.id}?tab=contract`));
      }
    }];
  });
}
